import React, { useMemo } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { MapPin } from 'lucide-react';
import type { Location } from '@/types';

interface LocationMarkerProps {
  location: Location;
  displayName?: string;
  onClick?: (location: Location) => void;
}

/**
 * Create location marker with home icon
 */
function createHomeLocationMarkerIcon(): L.DivIcon {
  return L.divIcon({
    html: `
      <div class="relative flex items-center justify-center">
        <div class="w-10 h-10 bg-orange-500 rounded-full border-3 border-white shadow-lg flex items-center justify-center relative z-10">
          <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
          </svg>
        </div>
        <div class="absolute w-14 h-14 bg-orange-500 rounded-full opacity-20 -z-10 animate-pulse"></div>
      </div>
    `,
    className: 'home-location-marker',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });
}

export const LocationMarker: React.FC<LocationMarkerProps> = React.memo(({
  location,
  displayName,
  onClick,
}) => {
  // Create location marker icon
  const markerIcon = useMemo(() => {
    return createHomeLocationMarkerIcon();
  }, []);

  const handleClick = React.useCallback(() => {
    onClick?.(location);
  }, [onClick, location]);

  return (
    <Marker
      position={[location.latitude, location.longitude]}
      icon={markerIcon}
      eventHandlers={{
        click: handleClick,
      }}
      // Higher z-index to appear above fuel station markers
      zIndexOffset={1000}
    >
      <Popup>
        <div className="min-w-[180px] p-2">
          <div className="font-semibold text-orange-600 mb-1 flex items-center">
            <MapPin className="w-4 h-4 mr-1" />
            Your Location
          </div>
          {displayName && (
            <div className="text-gray-700 text-sm">
              {displayName}
            </div>
          )}
          <div className="text-xs text-gray-500 mt-1">
            {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
          </div>
        </div>
      </Popup>
    </Marker>
  );
});

LocationMarker.displayName = 'LocationMarker';

export default LocationMarker;